import type { WebAdminPageData } from "../types/session.ts";
import type { ChartSettings } from "../types/chart.ts";
import { getClientLocale, getClientTimeZone, getWallClockInZone } from "./clientEnv.ts";

/**
 * Parse ChartConfig from a page's ClientOptions.
 * Mirrors WebAdmin2's AdminPageData.IsChart() logic:
 * searches for a ClientOption starting with "IsChart:" and
 * parses the JSON payload after the prefix.
 */
export function parseChartConfig(pageData: WebAdminPageData | undefined): ChartSettings[] | null {
  if (!pageData?.ClientOptions) return null;
  for (const option of pageData.ClientOptions) {
    if (option.startsWith("IsChart:")) {
      try {
        const json = option.substring(8);
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Extract a column name from a Webix-style template string.
 * Templates use #columnName# syntax, e.g. "#Month#" → "Month".
 * Returns the first match or null.
 */
export function parseTemplateColumn(template: unknown): string | null {
  if (typeof template !== "string") return null;
  const match = template.match(/#([^#]+)#/);
  return match?.[1] ?? null;
}

// ── IDS.Formatter compatibility shim ────────────────────────────
// Mirrors WebAdmin2's utility/formatter.ts methods so that chart config
// expressions referencing IDS.Formatter.* resolve correctly.

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatDatePart(d: Date): string {
  const w = getWallClockInZone(getClientTimeZone(), d);
  return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}

function formatTimePart(d: Date, includeSeconds: boolean): string {
  const w = getWallClockInZone(getClientTimeZone(), d);
  // Seconds aren't covered by the wall-clock helper (filter granularity is
  // minutes); read seconds straight off the Date — second-of-minute is
  // zone-invariant.
  return `${pad2(w.hour)}:${pad2(w.minute)}${includeSeconds ? ":" + pad2(d.getSeconds()) : ""}`;
}

/**
 * Matches WebAdmin2's FormatDateTime(value, includeSeconds, includeTimezone, includeTime, shortTimezone, toServerTime).
 */
function formatDateTime(
  value: unknown,
  includeSeconds = false,
  includeTimezone = false,
  includeTime = true,
): string {
  if (value == null) return "";
  const d = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(d.getTime())) return String(value);
  let out = formatDatePart(d);
  if (includeTime) out += " " + formatTimePart(d, includeSeconds);
  if (includeTimezone) {
    // Short timezone name from the browser
    try {
      const tz = new Intl.DateTimeFormat(getClientLocale(), { timeZone: getClientTimeZone(), timeZoneName: "short" }).formatToParts(d).find(p => p.type === "timeZoneName")?.value;
      if (tz) out += " " + tz;
    } catch {
      const offset = -d.getTimezoneOffset();
      const sign = offset >= 0 ? "+" : "-";
      out += ` UTC${sign}${pad2(Math.floor(Math.abs(offset) / 60))}:${pad2(Math.abs(offset) % 60)}`;
    }
  }
  return out;
}

function formatDate(value: unknown, includeTime = false, includeSeconds = false): string {
  return formatDateTime(value, includeSeconds, false, includeTime);
}

function formatTime(value: unknown, includeSeconds = false): string {
  if (value == null) return "";
  const d = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(d.getTime())) return String(value);
  return formatTimePart(d, includeSeconds);
}

/** The IDS.Formatter shim object, injected into chart config eval scopes. */
export const IDSFormatterShim = {
  Formatter: {
    FormatDateTime: formatDateTime,
    FormatDate: formatDate,
    FormatTime: formatTime,
  },
};

// ── Layout property resolution ──────────────────────────────────
// Handles __:eval: and __:#: prefixes in chart layout config,
// mirroring WebAdmin2's InnerChart.CheckIfFunction.

/**
 * Resolve __: prefixed layout values into functions or column indices.
 * - "__:eval:..." → compiled function
 * - "__:#:ColumnName" → column index number
 * - "__:FormatDateTime:ColumnName" → date formatting function
 * Recurses into nested objects.
 */
export function resolveLayoutProperties(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  block: Record<string, any>,
  indexOfHeader: (name: string) => number,
): void {
  for (const key of Object.keys(block)) {
    const item = block[key];
    if (item && typeof item === "object" && !Array.isArray(item) && !(item instanceof Date)) {
      resolveLayoutProperties(item, indexOfHeader);
    } else if (typeof item === "string" && item.startsWith("__:")) {
      const colonIdx = item.indexOf(":", 3);
      const directive = colonIdx > 0 ? item.substring(3, colonIdx).toLowerCase() : item.substring(3).toLowerCase();
      const arg = colonIdx > 0 ? item.substring(colonIdx + 1) : "";

      switch (directive) {
        case "eval": {
          try {
            const fn = new Function("IDS", arg);
            block[key] = (row: unknown[]) => {
              try { return fn(IDSFormatterShim, row); }
              catch { return ""; }
            };
          } catch (e) {
            console.error("Failed to compile __:eval:", e);
            block[key] = () => "";
          }
          break;
        }
        case "#": {
          const idx = indexOfHeader(arg);
          block[key] = idx >= 0 ? idx : arg;
          break;
        }
        case "formatdatetime": {
          const idx = indexOfHeader(arg);
          const colIdx = idx >= 0 ? idx : arg;
          block[key] = (row: unknown[]) => formatDateTime(row[colIdx as number], false, false, false);
          break;
        }
        default:
          break;
      }
    }
  }
}

/**
 * Strip HTML tags from a string for plain-text display (SVG title elements).
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&#0?176;/g, "°")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
